import { useMutation, useQuery } from 'react-query'

// Libs
import { addFollowingMember } from '@/api/addFollowingMember'
import { deleteFollowingMember } from '@/api/deleteFollowingMember'
import { fetchFollowers, ParamsType } from '@/api/fetchFollowers'
import { fetchFollowingMembers, ParamsType as FetchFollowingParamsType } from '@/api/fetchFollowingMembers'
import { fetchIsMemberFollowed } from '@/api/fetchIsMemberFollowed'
import { QUERY_KEY } from '@/constants/queryKey'
import { useMemberData } from '../useMemberData'
import { USER_ROLE } from '@/types/user-profile'
import { useAppSelector } from '@/redux/hooks'

// Constants

type UseFollowingMembersType = {
  mutationCallback?: () => void
  memberInfoId?: string
  followerType?: USER_ROLE
  followeeType?: USER_ROLE
  enabled?: boolean
}

type TFollowMutationParams = {
  followeeId: string
  followeeType: USER_ROLE
  followerType: USER_ROLE
}

export const useFollowingMembers = (props?: UseFollowingMembersType & FetchFollowingParamsType) => {
  const { mutationCallback, role, followerType, followeeType, enabled } = props || {}
  const { memberData } = useAppSelector((state) => state.member)
  const memberIdToQuery = props?.memberInfoId || memberData.memberInfoId

  const { data, refetch } = useQuery(
    [QUERY_KEY.FETCH_FOLLOWING_MEMBERS, { role, memberIdToQuery, followerType, followeeType }],
    () =>
      fetchFollowingMembers({
        role,
        memberInfoId: memberIdToQuery,
        followerType,
        followeeType,
      }),
    {
      enabled: !!memberIdToQuery && enabled,
    },
  )
  const addFollowingMemberMutation = useMutation(addFollowingMember)
  const deleteFollowingMemberMutation = useMutation(deleteFollowingMember)

  const handleAddFollowingMember = async ({
    followeeId,
    followeeType,
    followerType,
  }: {
    followeeId: string
    followeeType: USER_ROLE
    followerType: USER_ROLE
  }) => {
    try {
      await addFollowingMemberMutation.mutateAsync({
        data: {
          followeeId,
          followerId: memberData.memberInfoId,
          followerType,
          followeeType,
        },
      })
      refetch()
      mutationCallback && mutationCallback()
      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: false,
      }
    }
  }
  const handleUnFollowMember = async ({ followeeId, followeeType, followerType }: TFollowMutationParams) => {
    try {
      await deleteFollowingMemberMutation.mutateAsync({
        followeeId,
        followerId: memberData.memberInfoId,
        followeeType,
        followerType,
      })
      refetch()
      mutationCallback && mutationCallback()
      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: false,
      }
    }
  }

  return {
    handleAddFollowingMember,
    handleUnFollowMember,
    followingMembers: data?.memberInfo || [],
    refetchFollowingMembers: refetch,
  }
}
export const useFollowingActions = ({
  mutationCallback,
  followeeType,
  followerType,
}: UseFollowingMembersType & { followeeId: string; followeeType: USER_ROLE; followerType: USER_ROLE }) => {
  const addFollowingMemberMutation = useMutation(addFollowingMember)
  const deleteFollowingMemberMutation = useMutation(deleteFollowingMember)
  const { memberData } = useMemberData()
  const handleAddFollowingMember = async ({ followeeId }: { followeeId: string }) => {
    try {
      await addFollowingMemberMutation.mutateAsync({
        data: {
          followeeId,
          followerId: memberData.memberInfoId,
          followerType,
          followeeType,
        },
      })
      mutationCallback && mutationCallback()
      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: false,
      }
    }
  }
  const handleUnFollowMember = async ({ followeeId }: { followeeId: string }) => {
    try {
      await deleteFollowingMemberMutation.mutateAsync({
        followeeId,
        followerId: memberData.memberInfoId,
        followeeType,
        followerType,
      })
      mutationCallback && mutationCallback()
      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: false,
      }
    }
  }
  return {
    handleAddFollowingMember,
    handleUnFollowMember,
  }
}

export const useFollowers = (props?: ParamsType) => {
  const { role, memberInfoId, followeeType } = props || {}
  const { data, refetch } = useQuery(
    [QUERY_KEY.FETCH_FOLLOWERS, { role, memberInfoId, followeeType }],
    () =>
      fetchFollowers({
        role,
        memberInfoId,
        followeeType,
      }),
    {
      enabled: !!memberInfoId,
    },
  )
  return {
    followers: data?.memberInfo || [],
    refetchFollowers: refetch,
  }
}

export const useIsMemberFollowed = (props?: ParamsType) => {
  const { memberInfoId } = props || {}
  const query = useQuery(
    [QUERY_KEY.FETCH_IS_MEMBER_FOLLOWED, { memberInfoId }],
    () =>
      fetchIsMemberFollowed({
        memberInfoId,
      }),
    {
      enabled: !!memberInfoId,
    },
  )
  const { data, refetch } = query
  return {
    isMemberFollowed: data?.isFollowed || false,
    refetchIsMemberFollowed: refetch,
    isFetchMemberFollowedSuccess: query.isSuccess,
  }
}
